import { useState } from "react";
import { ProductModel } from "@/types";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/lib/AnalyticsContext";
import { usePlayer } from "@/lib/PlayerContext";
import { tryEnterPiP, isIOS } from "@/lib/pip";
import { IoIosArrowBack } from "react-icons/io";
import { getOrCreateGaUserId } from "@/lib/gaUserId";

export interface ProductListProps {
  products: ProductModel[];
  onSelectProduct?: (product: ProductModel) => void;
  selectedProduct?: ProductModel | null;
  fullScreen?: boolean;
  playerId?: string;
}

const ProductImage = ({ src, alt }: { src: string; alt: string }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div className="tw-aspect-square tw-relative tw-overflow-hidden tw-rounded-lg tw-bg-gray-100">
      {!isLoaded && (
        <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
          <div className="tw-w-8 tw-h-8 tw-border-4 tw-border-gray-200 tw-border-t-gray-400 tw-rounded-full tw-animate-spin"></div>
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`tw-w-full tw-h-full tw-object-cover tw-transition-opacity tw-duration-300 ${
          isLoaded ? "tw-opacity-100" : "tw-opacity-0"
        }`}
        onLoad={() => setIsLoaded(true)}
      />
    </div>
  );
};

const ProductList = ({
  products,
  onSelectProduct,
  selectedProduct,
  fullScreen = false,
  playerId,
}: ProductListProps) => {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const [iframeProduct, setIframeProduct] = useState<ProductModel | null>(null);
  const [detailProduct, setDetailProduct] = useState<ProductModel | null>(null);
  const { meta } = usePlayer();

  // 商品をタップした時の処理
  const handleProductClick = (product: ProductModel) => {
    // iOS/iPad では商品選択時に PiP を開始
    if (isIOS() && playerId) {
      tryEnterPiP(playerId);
    }
    
    // iframeUrlがある場合はiframe表示
    if (product.iframeUrl && product.iframeUrl.trim() !== "") {
      setIframeProduct(product);
    } else {
      // 商品詳細を表示
      setDetailProduct(product);
    }
    
    analytics.sendLiveCommerceEvent.viewProduct(product.id, meta.id);
  };

  // 購入ボタンをクリックした時の処理
  const handlePurchaseClick = (e: React.MouseEvent, product: ProductModel) => {
    e.stopPropagation();
    if (!product.url) return;
    
    // iOS/iPad 以外では購入ボタンで PiP を開始
    if (!isIOS() && playerId) {
      tryEnterPiP(playerId);
    }
    
    // ecProductIdがある場合はクエリパラメータを付与
    let targetUrl = product.url;
    if (product.ecProductId) {
      try {
        const url = new URL(product.url);
        url.searchParams.set('fv_episode_id', meta.id || '');
        url.searchParams.set('fv_user_id', getOrCreateGaUserId());
        url.searchParams.set('fv_product_id', product.id);
        targetUrl = url.toString();
      } catch (e) {
        // URL解析に失敗した場合は元のURLを使用
        console.warn('Failed to parse product URL:', e);
      }
    }
    
    // window.open をユーザージェスチャー内で直接呼ぶ
    window.open(targetUrl, "_blank", "noopener,noreferrer");
  };

  const closeIframe = () => {
    setIframeProduct(null);
  };

  const closeDetail = () => {
    setDetailProduct(null);
  };

  // iframeの表示
  if (iframeProduct) {
    return (
      <div className="tw-p-4 tw-h-full">
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
          <button
            className="tw-text-gray-800 tw-bg-transparent tw-border-none tw-outline-none tw-flex tw-items-center hover:tw-cursor-pointer"
            onClick={closeIframe}
          >
            <IoIosArrowBack size="1.2em" />
            <span className="tw-ml-1">{t("common.back")}</span>
          </button>
        </div>
        <div className="tw-h-full tw-rounded-lg tw-overflow-hidden tw-bg-gray-100">
          <iframe
            src={iframeProduct.iframeUrl}
            className="tw-w-full tw-h-full tw-border-0"
            title={iframeProduct.name}
            allowFullScreen
            loading="lazy"
            allow="payment; camera; microphone;"
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              zoom: fullScreen ? 1 : 0.65,
            }}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          ></iframe>
        </div>
      </div>
    );
  }

  // 商品詳細の表示
  if (detailProduct) {
    return (
      <div className="tw-p-4">
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
          <button
            className="tw-text-gray-800 tw-bg-transparent tw-border-none tw-outline-none tw-flex tw-items-center hover:tw-cursor-pointer"
            onClick={closeDetail}
          >
            <IoIosArrowBack size="1.2em" />
            <span className="tw-ml-1">{t("common.back")}</span>
          </button>
        </div>
        
        {/* 商品画像 */}
        <div className="tw-mb-4">
          <div className="tw-aspect-square tw-max-w-[200px] tw-mx-auto tw-rounded-lg tw-overflow-hidden tw-bg-gray-100">
            <img
              src={detailProduct.thumbnails?.[0] ?? ""}
              alt={detailProduct.name}
              className="tw-w-full tw-h-full tw-object-cover"
            />
          </div>
        </div>

        {/* 商品名 */}
        <div className="tw-font-bold tw-text-base tw-mb-2">
          {detailProduct.name}
        </div>

        {/* 商品説明 */}
        {detailProduct.description && (
          <div className="tw-py-1 tw-break-words tw-whitespace-pre-wrap tw-leading-6 tw-text-gray-600 tw-text-sm tw-mb-2">
            <div
              dangerouslySetInnerHTML={{
                __html: detailProduct.description,
              }}
            />
          </div>
        )}

        {/* 価格 */}
        {detailProduct.price && !isNaN(Number(detailProduct.price)) && (
          <div className="tw-font-semibold tw-text-green-600 tw-text-sm tw-mb-4">
            {String(
              t("product.price", {
                price: Number(detailProduct.price).toLocaleString(),
              })
            )}
          </div>
        )}

        {/* 購入ボタン */}
        {detailProduct.url && (
          <div className="tw-text-center">
            <button
              className="tw-bg-green-600 hover:tw-bg-green-500 tw-text-white tw-py-2 tw-px-6 tw-rounded-full tw-border-none tw-outline-none tw-shadow-md hover:tw-cursor-pointer tw-transition-colors tw-text-sm"
              onClick={(e) => handlePurchaseClick(e, detailProduct)}
            >
              {String(t("product.buy"))}
            </button>
          </div>
        )}
      </div>
    );
  }

  // 商品一覧の表示
  return (
    <div className="tw-p-4">
      <div className="tw-font-medium tw-text-lg tw-mb-4">
        {t("product.title")}
      </div>
      <div className="tw-grid tw-grid-cols-1 tw-gap-4">
        {products.map((product) => (
          <div
            key={product.id}
            className={`tw-cursor-pointer tw-p-2 tw-rounded-lg tw-transition-colors ${
              selectedProduct?.name === product.name
                ? "tw-bg-gray-100"
                : "hover:tw-bg-gray-50"
            }`}
            onClick={() => {
              if (product.url || product.iframeUrl) {
                handleProductClick(product);
              }
            }}
          >
            <div className="tw-flex tw-space-x-3">
              <div className="tw-w-24">
                <ProductImage
                  src={product.thumbnails?.[0] ?? ""}
                  alt={product.name}
                />
              </div>
              <div className="tw-flex-1">
                <div className="tw-font-medium tw-text-sm tw-mb-1 tw-line-clamp-2">
                  {product.name}
                </div>
                {product.price && !isNaN(Number(product.price)) && (
                  <div className="tw-text-sm tw-text-green-600">
                    {String(
                      t("product.price", {
                        price: Number(product.price).toLocaleString(),
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductList;
